import { Component, Injector } from '@angular/core';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import { Observable, filter, tap } from 'rxjs';

import { CuttingFacadeSlot } from '../../../models/cutting/cutting-facade-slot.model';
import { CuttingHandle } from '../../../models/cutting/cutting-handle.model';
import { OutlineSide } from '../../../models/cutting/cutting-panel-outline.model';
import { Position } from '../../../models/cutting/cutting-svg.model';
import { FacadeView } from '../../../models/cutting/facade-view.model';
import { LegacyDialogsService } from '../../../services/legacy-dialogs.service';
import { HandlesDialogComponent, MaterialBaseHandlesDialogData } from '../../handles-dialog/handles-dialog.component';
import { HandlesDialogService } from '../../handles-dialog/handles-dialog.service';
import { FacadeSettingsStateService } from '../facade-settings-state.service';
import { FacadesAvailableComponentsService } from '../facades-available-components.service';

export class HandleBase {}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _HandleMixinBase = mixinDisabled(HandleBase);

@Component({
  selector: 'csl-handle',
  templateUrl: './handle.component.html',
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
})
export class HandleComponent extends _HandleMixinBase implements CanDisable {
  sidesWithAngleProcessing: Set<Position> = new Set();
  notAvailableToAdd = false;
  topNotAvailableToAdd = false;
  bottomNotAvailableToAdd = false;
  leftNotAvailableToAdd = false;
  rightNotAvailableToAdd = false;

  panel$: Observable<FacadeView>;
  action$: Observable<any>;

  constructor(
    private facadeSettingsService: FacadeSettingsStateService,
    private dialogs: LegacyDialogsService,

    private facadesAvailableComponentsService: FacadesAvailableComponentsService,
    private injector: Injector
  ) {
    super();

    this.panel$ = this.facadeSettingsService.panel$.pipe(
      tap(p => {
        this.checkSidesAvailability(p);
      })
    );
  }

  checkSidesAvailability(panel: FacadeView): void {
    this.sidesWithAngleProcessing = this.getSidesWithAngleProcessing(panel);
    this.notAvailableToAdd = this.isNotAvailableToAdd();
    this.topNotAvailableToAdd = this.isTopNotAvailableToAdd();
    this.bottomNotAvailableToAdd = this.isBottomNotAvailableToAdd();
    this.leftNotAvailableToAdd = this.isLeftNotAvailableToAdd();
    this.rightNotAvailableToAdd = this.isRightNotAvailableToAdd();
  }

  isNotAvailableToAdd(): boolean {
    return this.isTopNotAvailableToAdd() && this.isBottomNotAvailableToAdd();
    //  && this.isLeftNotAvailableToAdd() && this.isRightNotAvailableToAdd();
  }

  isTopNotAvailableToAdd() {
    return this.sidesWithAngleProcessing.has('tc');
  }

  isBottomNotAvailableToAdd() {
    return this.sidesWithAngleProcessing.has('bc');
  }

  isLeftNotAvailableToAdd() {
    return this.sidesWithAngleProcessing.has('lc');
  }

  isRightNotAvailableToAdd() {
    return this.sidesWithAngleProcessing.has('rc');
  }

  getSidesWithAngleProcessing(panel: FacadeView): Set<Position> {
    const sides = new Set<Position>();

    if (!panel) {
      return sides;
    }

    if (panel.panelOutline.topLeft.mode) {
      sides.add('tc');
      sides.add('lc');
    }

    if (panel.panelOutline.topRight.mode) {
      sides.add('tc');
      sides.add('rc');
    }

    if (panel.panelOutline.bottomRight.mode) {
      sides.add('bc');
      sides.add('rc');
    }

    if (panel.panelOutline.bottomLeft.mode) {
      sides.add('bc');
      sides.add('lc');
    }

    return sides;
  }

  getDescription(panel: FacadeView): string {
    const settedHandle = this.getSetedHandle(panel);

    return settedHandle
      ? settedHandle.name
      : this.notAvailableToAdd
      ? $localize`Невозможно добавить из-за обработки угла`
      : $localize`Выберите ручку`;
  }

  getSetedHandle(panel: FacadeView): CuttingHandle {
    return (
      panel?.panelOutline.topSide.handle ||
      panel?.panelOutline.rightSide.handle ||
      panel?.panelOutline.bottomSide.handle ||
      panel?.panelOutline.leftSide.handle
    );
  }

  remove(panel: FacadeView): void {
    panel.panelOutline.topSide.handle = null;
    panel.panelOutline.rightSide.handle = null;
    panel.panelOutline.bottomSide.handle = null;
    panel.panelOutline.leftSide.handle = null;

    this.facadeSettingsService.updatePanel(panel);
  }

  getPosition(panel: FacadeView): Position {
    if (panel.panelOutline.topSide.handle) {
      return 'tc';
    }
    if (panel.panelOutline.rightSide.handle) {
      return 'rc';
    }
    if (panel.panelOutline.bottomSide.handle) {
      return 'bc';
    }
    if (panel.panelOutline.leftSide.handle) {
      return 'lc';
    }
  }

  changeHandle(panel: FacadeView): void {
    this.action$ = this.getHandlesFromMatBase().pipe(
      filter(r => !!r),
      tap((edge: CuttingHandle) => {
        const side = this.getSide(this.getPosition(panel), panel);

        side.handle = edge;

        this.facadeSettingsService.updatePanel(panel);
      })
    );
  }

  private getSide(position: Position, panel: FacadeView): OutlineSide<CuttingFacadeSlot> {
    switch (position) {
      case 'tc':
        return panel.panelOutline.topSide;
      case 'rc':
        return panel.panelOutline.rightSide;
      case 'bc':
        return panel.panelOutline.bottomSide;
      case 'lc':
        return panel.panelOutline.leftSide;

      default:
        return !this.topNotAvailableToAdd
          ? panel.panelOutline.topSide
          : !this.bottomNotAvailableToAdd
          ? panel.panelOutline.bottomSide
          : !this.leftNotAvailableToAdd
          ? panel.panelOutline.leftSide
          : !this.rightNotAvailableToAdd
          ? panel.panelOutline.rightSide
          : undefined;
    }
  }

  private getHandlesFromMatBase(): Observable<CuttingHandle> {
    const parentInjector = this.injector;

    return this.dialogs.component<MaterialBaseHandlesDialogData, CuttingHandle>(
      HandlesDialogComponent,
      {
        dataProvider: this.facadesAvailableComponentsService,
      },
      1000,
      {
        injector: Injector.create({
          providers: [{ provide: HandlesDialogService, useValue: HandlesDialogService }],
          parent: parentInjector,
        }),
      }
    );
  }

  changePosition(val: Position, panel: FacadeView): void {
    [
      panel.panelOutline.topSide.handle,
      panel.panelOutline.rightSide.handle,
      panel.panelOutline.bottomSide.handle,
      panel.panelOutline.leftSide.handle,
    ] = [panel.panelOutline.bottomSide.handle, null, panel.panelOutline.topSide.handle, null];
  }
}
